import { StageDef } from '../automationMachineInterfaces'
import {
  getCurrentValueForRadioButtons,
  saveCurrentValueForRadioButtons,
  toggleRadioButtonWithWebRequest,
} from './radioButtonUtils'
import { STRAVA_AUTOMATION_STAGE_STATUSES } from './stravaAutomationStageStatuses'
import * as stravaUrls from './stravaUrls'
import { StravaSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

const sectionTitle = 'Where You Appear'

const MENU_TITLES = {
  PROFILE: 'Profile Page',
  ACTIVITY: 'Activities',
  GROUP_ACTIVITIES: 'Group Activities',
}

const PROFILE_SETTINGS = {
  FOLLOWERS: 'Followers',
  EVERYONE: 'Everyone',
}

const ACTIVITIES_SETTINGS = {
  ONLY_YOU: 'Only You',
  FOLLOWERS: 'Followers',
  EVERYONE: 'Everyone',
}

const GROUP_ACTIVITIES_SETTINGS = {
  NO_ONE: 'No One',
  FOLLOWERS: 'Followers',
  EVERYONE: 'Everyone',
}

const PROFILE_WEB_REQUEST_PAYLOAD = {
  [PROFILE_SETTINGS.EVERYONE]: { profile_visibility: 'everyone' },
  [PROFILE_SETTINGS.FOLLOWERS]: { profile_visibility: 'followers_only' },
}

const ACTIVITIES_WEB_REQUEST_PAYLOAD = {
  [ACTIVITIES_SETTINGS.ONLY_YOU]: { activity_visibility: 'only_me' },
  [ACTIVITIES_SETTINGS.FOLLOWERS]: { activity_visibility: 'followers_only' },
  [ACTIVITIES_SETTINGS.EVERYONE]: { activity_visibility: 'everyone' },
}

const GROUP_ACTIVITIES_WEB_REQUEST_PAYLOAD = {
  [GROUP_ACTIVITIES_SETTINGS.NO_ONE]: { group_activity_visibility: 'no_one' },
  [GROUP_ACTIVITIES_SETTINGS.FOLLOWERS]: {
    group_activity_visibility: 'followers_only',
  },
  [GROUP_ACTIVITIES_SETTINGS.EVERYONE]: {
    group_activity_visibility: 'everyone',
  },
}

// Settings properties
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => value)

const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(
    key,
    (value) =>
      value === 'Followers' || value === 'Only You' || value === 'No One',
  )

const getCantUndoFn = () => defaultCantUndoFn

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

// Setup gather stage definitions

enum StageKey {
  GATHER_SETTING_PROFILE_VISIBILITY = 'GATHER_SETTING_PROFILE_VISIBILITY',
  UPDATE_SETTING_PROFILE_VISIBILITY = 'UPDATE_SETTING_PROFILE_VISIBILITY',
  UNDO_SETTING_PROFILE_VISIBILITY = 'UNDO_SETTING_PROFILE_VISIBILITY',
  GATHER_SETTING_ACTIVITIES_VISIBILITY = 'GATHER_SETTING_ACTIVITIES_VISIBILITY',
  UPDATE_SETTING_ACTIVITIES_VISIBILITY = 'UPDATE_SETTING_ACTIVITIES_VISIBILITY',
  UNDO_SETTING_ACTIVITIES_VISIBILITY = 'UNDO_SETTING_ACTIVITIES_VISIBILITY',
  GATHER_SETTING_GROUP_ACTIVITIES_VISIBILITY = 'GATHER_SETTING_GROUP_ACTIVITIES_VISIBILITY',
  UPDATE_SETTING_GROUP_ACTIVITIES_VISIBILITY = 'UPDATE_SETTING_GROUP_ACTIVITIES_VISIBILITY',
  UNDO_SETTING_GROUP_ACTIVITIES_VISIBILITY = 'UNDO_SETTING_GROUP_ACTIVITIES_VISIBILITY',
  UPDATE_VISIBILITY_FINALIZE = 'UPDATE_VISIBILITY_FINALIZE',
  UNDO_VISIBILITY_FINALIZE = 'UNDO_VISIBILITY_FINALIZE',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_PROFILE_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForRadioButtons(
        userContext.updateStoredInitialValues,
        PROFILE_SETTINGS,
        StravaSettingKey.PROFILE_VISIBILITY,
        enums.StravaRecommendationKey.STRAVA_VISIBILITY,
        sectionTitle,
        MENU_TITLES.PROFILE,
      )
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_ACTIVITIES_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForRadioButtons(
        userContext.updateStoredInitialValues,
        ACTIVITIES_SETTINGS,
        StravaSettingKey.ACTIVITIES_VISIBILITY,
        enums.StravaRecommendationKey.STRAVA_VISIBILITY,
        sectionTitle,
        MENU_TITLES.ACTIVITY,
      )
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_GROUP_ACTIVITIES_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForRadioButtons(
        userContext.updateStoredInitialValues,
        GROUP_ACTIVITIES_SETTINGS,
        StravaSettingKey.GROUP_ACTIVITIES_VISIBILITY,
        enums.StravaRecommendationKey.STRAVA_VISIBILITY,
        sectionTitle,
        MENU_TITLES.GROUP_ACTIVITIES,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_PROFILE_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry, userId } = userContext

      // Skip if not selected
      if (
        !settingsSelected ||
        !settingsSelected[StravaSettingKey.PROFILE_VISIBILITY]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForRadioButtons(
        sectionTitle,
        MENU_TITLES.PROFILE,
        PROFILE_SETTINGS,
      )

      // Toggle setting if incorrect one is selected
      if (!isDry && currentValue !== PROFILE_SETTINGS.FOLLOWERS) {
        await toggleRadioButtonWithWebRequest(userId, {
          athlete: PROFILE_WEB_REQUEST_PAYLOAD[PROFILE_SETTINGS.FOLLOWERS],
        })
      }
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_ACTIVITIES_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry, userId } = userContext

      // Skip if not selected
      if (
        !settingsSelected ||
        !settingsSelected[StravaSettingKey.ACTIVITIES_VISIBILITY]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForRadioButtons(
        sectionTitle,
        MENU_TITLES.ACTIVITY,
        ACTIVITIES_SETTINGS,
      )

      // Toggle setting if incorrect one is selected
      if (
        (!isDry && currentValue !== ACTIVITIES_SETTINGS.FOLLOWERS) ||
        currentValue !== ACTIVITIES_SETTINGS.ONLY_YOU
      ) {
        await toggleRadioButtonWithWebRequest(userId, {
          athlete:
            ACTIVITIES_WEB_REQUEST_PAYLOAD[ACTIVITIES_SETTINGS.FOLLOWERS],
        })
      }
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_GROUP_ACTIVITIES_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry, userId } = userContext

      // Skip if not selected
      if (
        !settingsSelected ||
        !settingsSelected[StravaSettingKey.GROUP_ACTIVITIES_VISIBILITY]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForRadioButtons(
        sectionTitle,
        MENU_TITLES.GROUP_ACTIVITIES,
        GROUP_ACTIVITIES_SETTINGS,
      )

      // Toggle setting if incorrect one is selected
      if (
        (!isDry && currentValue !== GROUP_ACTIVITIES_SETTINGS.FOLLOWERS) ||
        currentValue !== GROUP_ACTIVITIES_SETTINGS.NO_ONE
      ) {
        await toggleRadioButtonWithWebRequest(userId, {
          athlete:
            GROUP_ACTIVITIES_WEB_REQUEST_PAYLOAD[
              GROUP_ACTIVITIES_SETTINGS.FOLLOWERS
            ],
        })
      }
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_VISIBILITY_FINALIZE,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async () => {
      window.location.reload()
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_PROFILE_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry, userId } = userContext
      // Skip if not selected
      if (
        !initialValues ||
        !initialValues[StravaSettingKey.PROFILE_VISIBILITY]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForRadioButtons(
        sectionTitle,
        MENU_TITLES.PROFILE,
        PROFILE_SETTINGS,
      )

      // Toggle setting if incorrect one is selected
      if (
        !isDry &&
        currentValue !== initialValues[StravaSettingKey.PROFILE_VISIBILITY]
      ) {
        await toggleRadioButtonWithWebRequest(userId, {
          athlete:
            PROFILE_WEB_REQUEST_PAYLOAD[
              initialValues[StravaSettingKey.PROFILE_VISIBILITY]
            ],
        })
      }
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_ACTIVITIES_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry, userId } = userContext

      // Skip if not selected
      if (
        !initialValues ||
        !initialValues[StravaSettingKey.ACTIVITIES_VISIBILITY]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForRadioButtons(
        sectionTitle,
        MENU_TITLES.ACTIVITY,
        ACTIVITIES_SETTINGS,
      )

      // Toggle setting if incorrect one is selected
      if (
        !isDry &&
        currentValue !== initialValues[StravaSettingKey.ACTIVITIES_VISIBILITY]
      ) {
        await toggleRadioButtonWithWebRequest(userId, {
          athlete:
            ACTIVITIES_WEB_REQUEST_PAYLOAD[
              initialValues[StravaSettingKey.ACTIVITIES_VISIBILITY]
            ],
        })
      }
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_GROUP_ACTIVITIES_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry, userId } = userContext

      // Skip if not selected
      if (
        !initialValues ||
        !initialValues[StravaSettingKey.GROUP_ACTIVITIES_VISIBILITY]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForRadioButtons(
        sectionTitle,
        MENU_TITLES.GROUP_ACTIVITIES,
        GROUP_ACTIVITIES_SETTINGS,
      )

      // Toggle setting if incorrect one is selected
      if (
        !isDry &&
        currentValue !==
          initialValues[StravaSettingKey.GROUP_ACTIVITIES_VISIBILITY]
      ) {
        await toggleRadioButtonWithWebRequest(userId, {
          athlete:
            GROUP_ACTIVITIES_WEB_REQUEST_PAYLOAD[
              initialValues[StravaSettingKey.GROUP_ACTIVITIES_VISIBILITY]
            ],
        })
      }
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_VISIBILITY_FINALIZE,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async () => {
      window.location.reload()
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
